/**
 * Euler angles in degrees, `[x, y, z]` = `[pitch, yaw, roll]`: the authoring
 * and display format for `Transform.rotation`.
 *
 * Rotation order is intrinsic Y-X-Z (yaw, then pitch in the yawed frame, then
 * roll), i.e. three.js Euler order 'YXZ' and q = qy · qx · qz. This matches
 * Unreal's rotators and Unity's transforms: a camera authored as [-13, 25, 0]
 * turns 25° and then looks 13° down, which is what an author expects.
 */
import type { Quat } from "./quat";
import type { Vec3 } from "./vec3";

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

/** Beyond this |sin(pitch)| the decomposition is treated as gimbal-locked. */
const GIMBAL_LOCK_THRESHOLD = 0.9999999;

/** Converts YXZ Euler degrees to a unit quaternion (three's `setFromEuler`, order 'YXZ'). */
export function eulerDegToQuat(euler: Vec3): Quat {
  const c1 = Math.cos((euler[0] * DEG_TO_RAD) / 2);
  const c2 = Math.cos((euler[1] * DEG_TO_RAD) / 2);
  const c3 = Math.cos((euler[2] * DEG_TO_RAD) / 2);
  const s1 = Math.sin((euler[0] * DEG_TO_RAD) / 2);
  const s2 = Math.sin((euler[1] * DEG_TO_RAD) / 2);
  const s3 = Math.sin((euler[2] * DEG_TO_RAD) / 2);

  return [
    s1 * c2 * c3 + c1 * s2 * s3,
    c1 * s2 * c3 - s1 * c2 * s3,
    c1 * c2 * s3 - s1 * s2 * c3,
    c1 * c2 * c3 + s1 * s2 * s3,
  ];
}

/**
 * The canonical YXZ decomposition of a quaternion: pitch (X) in [-90°, 90°].
 * Mirrors three's `Euler.setFromRotationMatrix` for order 'YXZ'.
 */
export function quatToEulerDeg(q: Quat): Vec3 {
  const [x, y, z, w] = q;

  // Only the rotation-matrix entries the YXZ decomposition needs (row, column).
  const m11 = 1 - 2 * (y * y + z * z);
  const m13 = 2 * (x * z + y * w);
  const m21 = 2 * (x * y + z * w);
  const m22 = 1 - 2 * (x * x + z * z);
  const m23 = 2 * (y * z - x * w);
  const m31 = 2 * (x * z - y * w);
  const m33 = 1 - 2 * (x * x + y * y);

  const pitch = Math.asin(-Math.min(1, Math.max(-1, m23)));

  if (Math.abs(m23) < GIMBAL_LOCK_THRESHOLD) {
    return [
      pitch * RAD_TO_DEG,
      Math.atan2(m13, m33) * RAD_TO_DEG,
      Math.atan2(m21, m22) * RAD_TO_DEG,
    ];
  }

  // Gimbal lock: yaw and roll rotate about the same axis, so fold everything into yaw.
  return [pitch * RAD_TO_DEG, Math.atan2(-m31, m11) * RAD_TO_DEG, 0];
}

/**
 * Decomposes `q` into the YXZ Euler angles closest to `previous`.
 *
 * Every rotation has two decompositions, (x, y, z) and (180 − x, y + 180, z + 180),
 * and each angle is also equivalent modulo 360. Picking the canonical one makes
 * values jump mid-drag (pitch 80° → 100° would become (80, 180, 180)). Like
 * Unreal's rotator handling, we pick whichever equivalent is nearest to the
 * value the user was already looking at (R-5).
 */
export function quatToEulerDegClosest(q: Quat, previous: Vec3): Vec3 {
  const canonical = quatToEulerDeg(q);
  const alternate: Vec3 = [180 - canonical[0], canonical[1] + 180, canonical[2] + 180];

  const a = unwrapTowards(canonical, previous);
  const b = unwrapTowards(alternate, previous);
  return angularDistance(a, previous) <= angularDistance(b, previous) ? a : b;
}

/** Shifts each angle by multiples of 360° so it lies within 180° of the reference. */
function unwrapTowards(angles: Vec3, reference: Vec3): Vec3 {
  return [
    unwrapAngle(angles[0], reference[0]),
    unwrapAngle(angles[1], reference[1]),
    unwrapAngle(angles[2], reference[2]),
  ];
}

function unwrapAngle(angle: number, reference: number): number {
  return angle + 360 * Math.round((reference - angle) / 360);
}

function angularDistance(a: Vec3, b: Vec3): number {
  return Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + Math.abs(a[2] - b[2]);
}
